import { type ReactNode, type RefCallback, type RefObject } from 'react';

import type { NavItemConfigWithCustomizationOptions } from '../../types';
import {
	createNestedItemData,
	getNestedItemData,
	type IsItemDraggable,
	isNestedItemData,
	isNestedItemDataInGroup,
	type RawItemData,
	type TNestedItemData,
} from '../dnd-data';
import type { AnalyticsEventHandler } from '../use-item-drop';

import { useItemDragAndDrop } from './index';

export interface NestedItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragAndDropEnabled?: boolean;
	isDragging: boolean;
	isItemDraggable: boolean;
	maybeDragPreview: ReactNode;
}

type UseNestedItemDragAndDrop = (args: {
	data: RawItemData;
	entityAri: string;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	group: string;
	id: string;
	isDragAndDropEnabled?: boolean;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	item?: NavItemConfigWithCustomizationOptions;
	onExpandFlyoutToggle: (open: boolean) => void;
	ownerAri: string;
}) => NestedItemDragAndDrop;

export const useNestedItemDragAndDrop: UseNestedItemDragAndDrop = ({
	data,
	group,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	item,
	onExpandFlyoutToggle,
	isDragAndDropEnabled,
	entityAri,
	ownerAri,
	fireAnalyticsEvent,
}) => {
	const baseData = {
		menuItemId: id,
		group,
		isReorderable: true,
		entityAri,
		ownerAri,
		label: data.label,
	};
	const nestedItemData = createNestedItemData(baseData);
	const isItemDraggable: IsItemDraggable<TNestedItemData> = () => {
		return isDragAndDropEnabled !== false && nestedItemData.entityAri === entityAri;
	};

	return useItemDragAndDrop<TNestedItemData>({
		data: nestedItemData,
		fireAnalyticsEvent,
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isDragAndDropEnabled,
		isItemDraggable,
		group,
		getItemData: getNestedItemData,
		isItemData: isNestedItemData,
		isItemDataInGroup: isNestedItemDataInGroup,
	});
};
